import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createSlotRequest,
  deleteSlotRequest,
  getSlotsRequest,
  updateSlotRequest,
  uploadSlotsRequest,
} from './slotsApi';
import { ALL_ZONES } from './slotsConstants';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the locations the filters describe, in the warehouse in use — or in
 * every warehouse in reach when none is picked.
 *
 * @returns {Promise<Array<object>>}
 */
export const fetchSlots = createAsyncThunk(
  'slots/fetchSlots',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { search, zoneType } = state.slots.filters;

      const params = {};
      const warehouseId = selectCurrentWarehouseId(state);
      if (warehouseId) params.warehouse = warehouseId;
      if (zoneType !== ALL_ZONES) params.zone_type = zoneType;
      if (search) params.search = search;

      const response = await getSlotsRequest(params);
      return response.data?.slots || [];
    } catch (error) {
      console.error('Error fetching slots:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las ubicaciones'));
    }
  }
);

/**
 * The active locations of one warehouse, for the pickers that send stock
 * somewhere. Kept apart from the listing, whose filters must not decide where
 * stock can go.
 *
 * @param {number} warehouseId
 * @returns {Promise<{warehouseId: number, slots: Array<object>}>}
 */
export const fetchSlotOptions = createAsyncThunk(
  'slots/fetchSlotOptions',
  async (warehouseId, { rejectWithValue }) => {
    try {
      const response = await getSlotsRequest({ warehouse: warehouseId, is_active: '1' });
      return { warehouseId, slots: response.data?.slots || [] };
    } catch (error) {
      console.error('Error fetching slot options:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las ubicaciones'));
    }
  }
);

/*
 * The three writes reload the listing instead of patching it: the order is the
 * server's (by walking rank, then name), the filters are applied there, and the
 * occupancy of every row is worked out there too.
 *
 * Their errors are read with `extractApiError` because the backend writes the
 * sentence for the person — "Ya existe la ubicación A-01 en CD Pudahuel" — and
 * a field error arrives as `{campo: [mensaje]}`.
 */

/**
 * @param {{warehouseId: number, values: object}} params - See `buildSlotPayload`.
 */
export const createSlot = createAsyncThunk(
  'slots/createSlot',
  async ({ warehouseId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await createSlotRequest({ warehouse_id: warehouseId, ...values });
      dispatch(fetchSlots());
      return response.data;
    } catch (error) {
      console.error('Error creating slot:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo crear la ubicación'));
    }
  }
);

/**
 * Loads a sheet of locations into a warehouse and reloads the listing.
 *
 * Rejects with `{message, errors}` rather than a string: the dialog lists the
 * rows the server refused, and a sentence alone would send the sheet back
 * blind. `extractApiError` keeps the server's sentence for the message.
 *
 * @param {{warehouseId: number, file: File}} params
 * @returns {Promise<{message: string, created: number}>}
 */
export const uploadSlots = createAsyncThunk(
  'slots/uploadSlots',
  async ({ warehouseId, file }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('warehouse_id', warehouseId);

      const response = await uploadSlotsRequest(formData);
      dispatch(fetchSlots());
      return response.data;
    } catch (error) {
      console.error('Error uploading slots:', error);
      return rejectWithValue({
        message: extractApiError(error, 'No se pudo cargar la planilla'),
        errors: error.response?.data?.errors || [],
      });
    }
  }
);

/**
 * @param {{slotId: number, values: object}} params - Only the fields to change.
 */
export const updateSlot = createAsyncThunk(
  'slots/updateSlot',
  async ({ slotId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateSlotRequest(slotId, values);
      dispatch(fetchSlots());
      return response.data;
    } catch (error) {
      console.error('Error updating slot:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar la ubicación'));
    }
  }
);

/**
 * Deletes a location nothing ever went through. One with stock or history is
 * refused by the server, which says to take it out of service instead.
 *
 * @param {number} slotId
 */
export const deleteSlot = createAsyncThunk(
  'slots/deleteSlot',
  async (slotId, { dispatch, rejectWithValue }) => {
    try {
      await deleteSlotRequest(slotId);
      dispatch(fetchSlots());
      return slotId;
    } catch (error) {
      console.error('Error deleting slot:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo eliminar la ubicación'));
    }
  }
);
